import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookmarkPlus, LayoutTemplate } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, errMsg } from "@/lib/api";
import { addBookingToSteps } from "@/lib/liSequences";

const LOCAL = "rcc_li_templates";
const local = () => { try { return JSON.parse(localStorage.getItem(LOCAL) || "[]"); } catch { return []; } };
const KEYS = ["first_name", "last_name", "company", "title", "open_roles", "target_role", "location", "industry"];
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Turn prospect-specific text into reusable placeholders, and back.
const toTpl = (t, p) => KEYS.filter((k) => p[k] && p[k].length > 1).sort((a, b) => p[b].length - p[a].length).reduce((s, k) => s.replace(new RegExp(`(?<![\\p{L}\\p{N}_])${esc(p[k])}(?![\\p{L}\\p{N}_])`, "gu"), `{${k}}`), t);
export const fromTpl = (t, p) => t.replace(/(?<!\{)\{(\w+)\}(?!\})/g, (m, k) => (KEYS.includes(k) ? p[k] || (k === "company" ? "your company" : "") : m));

export const useTemplates = (audience) => {
  const [list, setList] = useState([]);
  const [isLocal, setLocal] = useState(false);
  useEffect(() => { api.get(`/admin/rcc/li/templates?audience=${audience}`).then((r) => { setList(r.data?.items || []); setLocal(false); }).catch((e) => { if (e.response?.status === 404) { setLocal(true); setList(local().filter((t) => t.audience === audience)); } }); }, [audience]);
  const save = async (name, steps, p) => {
    const tpl = { name, audience, goal: p.goal || null, offers: p.offers || [], steps: steps.map((s) => ({ day: s.day, subject: toTpl(s.subject, p), body: toTpl(s.body, p) })), source_prospect_id: p.id };
    try { const { data } = await api.post("/admin/rcc/li/templates", tpl); setList((l) => [data, ...l]); }
    catch (e) { if (e.response?.status !== 404) throw e; const t = { ...tpl, id: `local-${Date.now()}`, created_at: new Date().toISOString() }; localStorage.setItem(LOCAL, JSON.stringify([t, ...local()])); setList((l) => [t, ...l]); setLocal(true); }
  };
  const used = (t, pid) => { if (!String(t.id).startsWith("local-")) api.post(`/admin/rcc/li/templates/${t.id}/use`, { prospect_id: pid }).catch(() => {}); };
  const sorted = [...list].sort((a, b) => (b.stats?.reply_rate ?? -1) - (a.stats?.reply_rate ?? -1));
  return { list: sorted, isLocal, save, used };
};

export const TemplateBar = ({ prospect: p, steps, setSteps, locked }) => {
  const t = useTemplates(p.audience || "hiring_manager");
  const saveAs = async () => {
    const name = window.prompt("Template name", `${p.audience === "job_seeker" ? "Job seeker" : "Hiring manager"} · ${new Date().toLocaleDateString()}`);
    if (!name) return;
    try { await t.save(name.trim(), steps, p); toast.success(`Saved “${name}”. Names and companies became placeholders.`); } catch (e) { toast.error(errMsg(e)); }
  };
  const apply = (tpl) => {
    if (!window.confirm(`Replace these 3 emails with “${tpl.name}”?`)) return;
    setSteps(addBookingToSteps(tpl.steps.map((s) => ({ day: s.day, subject: fromTpl(s.subject, p), body: fromTpl(s.body, p) })), p.audience, p.id));
    t.used(tpl, p.id);
    toast.success("Template applied and personalized");
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="li-templates">
      {!locked && <DropdownMenu><DropdownMenuTrigger asChild><Button size="sm" variant="outline" className="h-8 border-[#27272A]" data-testid="li-tpl-use"><LayoutTemplate className="h-3.5 w-3.5" />Use template{t.list.length ? ` (${t.list.length})` : ""}</Button></DropdownMenuTrigger>
        <DropdownMenuContent className="w-80 border-[#27272A] bg-[#121215]">
          {!t.list.length ? <p className="p-3 text-xs text-zinc-500">No templates yet. Save a sequence that works well.</p> : t.list.map((x, i) => (
            <DropdownMenuItem key={x.id} onClick={() => apply(x)} className="flex flex-col items-start" data-testid={`li-tpl-${i}`}>
              <span className="text-sm text-zinc-100">{x.name}</span>
              <span className="text-[10px] text-zinc-500">{x.stats ? `Used ${x.stats.used ?? 0}× · ${x.stats.reply_rate != null ? `${Math.round(x.stats.reply_rate * 100)}% replies` : "no replies yet"}` : "Saved on this device"}</span>
            </DropdownMenuItem>))}
        </DropdownMenuContent></DropdownMenu>}
      <Button size="sm" variant="outline" onClick={saveAs} disabled={!steps?.length} className="h-8 border-[#D4AF37]/50 text-[#F3C649]" data-testid="li-tpl-save"><BookmarkPlus className="h-3.5 w-3.5" />Save as template</Button>
      {t.isLocal && <span className="text-[10px] text-zinc-600">Saved in this browser until the templates route is live.</span>}
    </div>
  );
};
